 // =================================================================
 // RAÍZ CUADRADA — PHONE INPUT COMPONENT
 // Premium phone input with country code selector
 // =================================================================
 
 import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
 import { cn } from '@/lib/utils';
 import { ChevronDown, AlertTriangle, Check, Search } from 'lucide-react';
 import {
   Drawer,
   DrawerContent,
   DrawerHeader,
   DrawerTitle,
 } from '@/components/ui/drawer';
 import {
   Popover,
   PopoverContent,
   PopoverTrigger,
 } from '@/components/ui/popover';
 import { 
   COMMON_PHONE_CODES, 
   PhoneUtils, 
   getCountriesWithMexicoFirst,
  FlagIcon,
   type PhoneCode,
   type PhoneValidationResult 
 } from '@/lib/phone-codes';
 
 interface PhoneInputProps {
   value: string;
   onChange: (value: string, validation: PhoneValidationResult) => void;
   selectedCountry?: PhoneCode;
   onCountryChange?: (country: PhoneCode) => void;
   placeholder?: string;
   disabled?: boolean;
   showValidation?: boolean;
   className?: string;
   error?: string;
  size?: 'default' | 'compact';
 }
 
 export default function PhoneInput({
   value,
   onChange,
   selectedCountry: controlledCountry,
   onCountryChange,
   placeholder = '55 1234 5678',
   disabled = false,
   showValidation = true,
   className,
   error: externalError,
  size = 'default',
 }: PhoneInputProps) {
   const [isOpen, setIsOpen] = useState(false);
   const [searchTerm, setSearchTerm] = useState('');
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 639px)').matches;
  });
   const [internalCountry, setInternalCountry] = useState<PhoneCode>(
     COMMON_PHONE_CODES.find(c => c.iso2 === 'MX')!
   );
   const inputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
 
   const selectedCountry = controlledCountry || internalCountry;
 
   // Countries list with Mexico first
   const countries = getCountriesWithMexicoFirst();
 
   // Filter countries based on search
   const filteredCountries = countries.filter(country => 
     country.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
     country.countryEn.toLowerCase().includes(searchTerm.toLowerCase()) ||
     country.code.includes(searchTerm) ||
     country.dialCode.includes(searchTerm)
   );
 
   // Validate phone on change
   const validation = PhoneUtils.validatePhoneNumber(value, selectedCountry.code);
 
   useEffect(() => {
     if (typeof window === 'undefined') return undefined;

     const mediaQuery = window.matchMedia('(max-width: 639px)');
     const syncViewport = (matches: boolean) => setIsMobileViewport(matches);

     syncViewport(mediaQuery.matches);

     const handleChange = (event: MediaQueryListEvent) => {
       syncViewport(event.matches);
     };

     if (typeof mediaQuery.addEventListener === 'function') {
       mediaQuery.addEventListener('change', handleChange);
       return () => mediaQuery.removeEventListener('change', handleChange);
     }

     mediaQuery.addListener(handleChange);
     return () => mediaQuery.removeListener(handleChange);
   }, []);

   useEffect(() => {
     if (isOpen) {
       searchInputRef.current?.focus();
     }
   }, [isOpen]);

   useEffect(() => {
     if (!isOpen) {
       setSearchTerm('');
     }
   }, [isOpen]);
 
   const handleCountrySelect = useCallback((country: PhoneCode) => {
     if (onCountryChange) {
       onCountryChange(country);
     } else {
       setInternalCountry(country);
     }
     setIsOpen(false);
     setSearchTerm('');
     inputRef.current?.focus();
   }, [onCountryChange]);
 
   const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
     const newValue = e.target.value;
     // Only allow digits, spaces, and hyphens
     const sanitized = newValue.replace(/[^\d\s-]/g, '');
     const newValidation = PhoneUtils.validatePhoneNumber(sanitized, selectedCountry.code);
     onChange(sanitized, newValidation);
   };
 
   const hasError = externalError || (showValidation && value.length > 0 && !validation.isValid);
   const isValid = showValidation && value.length > 0 && validation.isValid;
  const isCompact = size === 'compact';

   const renderCountrySelectorTrigger = (onClick?: () => void) => (
     <button
       type="button"
       onClick={onClick}
       disabled={disabled}
       className={cn(
         isCompact ? 'h-11 px-3 text-sm' : 'h-14 px-3',
         'flex items-center gap-2 rounded-l-full border border-r-0',
         'bg-slate-50 dark:bg-zinc-900 border-slate-200 dark:border-zinc-800',
         'hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors',
         'disabled:opacity-50 disabled:cursor-not-allowed',
         isOpen && 'ring-2 ring-emerald-100 dark:ring-emerald-500/20'
       )}
       aria-label="Seleccionar código de país"
     >
        <FlagIcon 
          countryCode={selectedCountry.iso2} 
          size={isCompact ? 20 : 24} 
          fallbackEmoji={selectedCountry.flag}
          className="shrink-0"
        />
       <span className={cn(
         isCompact ? 'text-xs' : 'text-sm',
         'font-medium text-slate-700 dark:text-zinc-300',
       )}>
         {selectedCountry.code}
       </span>
       <ChevronDown className={cn(
         'size-4 text-slate-400 transition-transform',
         isOpen && 'rotate-180'
       )} />
     </button>
   );

   const countriesList = useMemo(() => (
     <>
       <div className="border-b border-slate-100 p-2 dark:border-zinc-800">
         <div className="relative">
           <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
           <input
             ref={searchInputRef}
             type="text"
             value={searchTerm}
             onChange={(e) => setSearchTerm(e.target.value)}
             placeholder="Buscar país o lada..."
             className="h-10 w-full rounded-xl border-0 bg-slate-50 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-100 dark:bg-zinc-800 dark:text-white dark:placeholder:text-zinc-500 dark:focus:ring-emerald-500/20"
           />
         </div>
       </div>

       <div className={cn('overflow-y-auto', isMobileViewport ? 'max-h-[60dvh]' : 'max-h-60')}>
         {filteredCountries.length > 0 ? (
           filteredCountries.map((country) => (
             <button
               key={`${country.iso2}-${country.code}`}
               type="button"
               onClick={() => handleCountrySelect(country)}
               className={cn(
                 'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors',
                 'hover:bg-slate-50 dark:hover:bg-zinc-800',
                 selectedCountry.iso2 === country.iso2 && selectedCountry.dialCode === country.dialCode &&
                   'bg-emerald-50 dark:bg-emerald-950/30'
               )}
             >
               <FlagIcon 
                 countryCode={country.iso2} 
                 size={24} 
                 fallbackEmoji={country.flag}
                 className="shrink-0"
               />
               <div className="min-w-0 flex-1">
                 <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                   {country.country}
                 </p>
                 {country.isCommonMigrant && (
                   <p className="text-[10px] text-emerald-600 dark:text-emerald-400">
                     Común en México
                   </p>
                 )}
               </div>
               <span className="font-mono text-sm text-slate-500 dark:text-zinc-500">
                 {country.code}
               </span>
               {selectedCountry.iso2 === country.iso2 && selectedCountry.dialCode === country.dialCode && (
                 <Check className="size-4 text-emerald-600" />
               )}
             </button>
           ))
         ) : (
           <div className="p-4 text-center text-sm text-slate-500 dark:text-zinc-500">
             No se encontraron países
           </div>
         )}
       </div>
     </>
   ), [filteredCountries, handleCountrySelect, isMobileViewport, searchTerm, selectedCountry.dialCode, selectedCountry.iso2]);

   const countrySelector = isMobileViewport ? (
     <Drawer open={isOpen} onOpenChange={setIsOpen} shouldScaleBackground>
       {renderCountrySelectorTrigger(() => !disabled && setIsOpen(!isOpen))}
       <DrawerContent side="bottom" className="z-[10040] rounded-t-[1.75rem] border border-slate-200 bg-white dark:border-zinc-800 dark:bg-zinc-950">
         <DrawerHeader className="pb-2 text-left">
           <DrawerTitle className="text-base font-semibold text-slate-900 dark:text-white">
             Selecciona tu país o lada
           </DrawerTitle>
         </DrawerHeader>
         <div className="pb-4">
           {countriesList}
         </div>
       </DrawerContent>
     </Drawer>
   ) : (
     <Popover open={isOpen} onOpenChange={setIsOpen}>
       <PopoverTrigger asChild>
         {renderCountrySelectorTrigger()}
       </PopoverTrigger>
       <PopoverContent side="bottom" align="start" sideOffset={10} className="z-[10050] w-[22rem] overflow-hidden rounded-2xl border border-slate-200 bg-white p-0 shadow-2xl dark:border-zinc-800 dark:bg-zinc-900">
         {countriesList}
       </PopoverContent>
     </Popover>
   );
 
   return (
     <div className={cn("w-full space-y-2", className)}>
       <div className="flex items-stretch">
         {/* Country Selector */}
         {countrySelector}
 
         {/* Phone Input */}
         <div className="relative flex-1">
           <input
             ref={inputRef}
             type="tel"
             value={value}
             onChange={handlePhoneChange}
             placeholder={placeholder}
             disabled={disabled}
             autoComplete="tel"
             className={cn(
              isCompact ? 'w-full h-11 px-3.5 pr-10 text-sm rounded-r-full border' : 'w-full h-14 px-4 pr-12 text-base rounded-r-full border',
               "bg-white dark:bg-zinc-950 text-slate-900 dark:text-white",
               "placeholder:text-slate-400 dark:placeholder:text-zinc-500",
               "transition-all duration-200",
               "disabled:opacity-50 disabled:cursor-not-allowed",
               hasError 
                 ? "border-rose-300 dark:border-rose-800 focus:ring-rose-100 dark:focus:ring-rose-500/20" 
                 : isValid
                   ? "border-emerald-300 dark:border-emerald-800 focus:ring-emerald-100 dark:focus:ring-emerald-500/20"
                   : "border-slate-200 dark:border-zinc-800 focus:ring-emerald-100 dark:focus:ring-emerald-500/20",
               "focus:outline-none focus:ring-2"
             )}
           />
 
           {/* Validation Icon */}
           {showValidation && value.length > 0 && (
            <div className={cn(isCompact ? 'right-3' : 'right-4', 'absolute top-1/2 -translate-y-1/2')}>
               {isValid ? (
                <Check className={cn(isCompact ? 'size-4' : 'size-5', 'text-emerald-500')} />
               ) : (
                <AlertTriangle className={cn(isCompact ? 'size-4' : 'size-5', 'text-rose-500')} />
               )}
             </div>
           )}
         </div>
       </div>
 
       {/* Validation Messages */}
       {(externalError || (showValidation && !validation.isValid && value.length > 0)) && (
         <div className="flex items-start gap-2 px-2">
           <AlertTriangle className="size-4 text-rose-500 shrink-0 mt-0.5" />
          <p className={cn(isCompact ? 'text-xs' : 'text-sm', 'text-rose-600 dark:text-rose-400')}>
             {externalError || validation.errorMessage || 'Número inválido'}
           </p>
         </div>
       )}
 
       {/* Success message with location */}
       {isValid && validation.internalCode && (
         <div className="flex items-center gap-2 px-2">
           <Check className="size-4 text-emerald-500 shrink-0" />
          <p className={cn(isCompact ? 'text-xs' : 'text-sm', 'text-emerald-600 dark:text-emerald-400')}>
             📍 {validation.internalCode.location}, {validation.internalCode.state}
           </p>
         </div>
       )}
     </div>
   );
 }